import React, { useCallback, useRef } from 'react';
import { Note } from '../../types/notes';

interface MinimapProps {
  notes: Note[];
  containerRef: React.RefObject<HTMLDivElement>;
  onNavigateToNote: (x: number, y: number) => void;
  isVisible: boolean;
}

export const Minimap: React.FC<MinimapProps> = ({ notes, containerRef, onNavigateToNote, isVisible }) => {
  const minimapRef = useRef<HTMLDivElement>(null);
  const minimapSize = 180;

  // Calculate the bounds of all notes to create a proper minimap scale
  const getNoteBounds = useCallback(() => {
    if (!notes.length) return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    notes.forEach(note => {
      const x = note.positionX || 0;
      const y = note.positionY || 0;
      const width = note.width || 250;
      const height = note.height || 180;
      
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    });

    // Add padding to the bounds
    const padding = 100;
    return {
      minX: minX - padding,
      minY: minY - padding,
      maxX: maxX + padding,
      maxY: maxY + padding
    };
  }, [notes]);

  const handleMinimapClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!minimapRef.current || !containerRef?.current) return;

    const rect = minimapRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - 10; // Account for padding
    const y = e.clientY - rect.top - 10;

    // Convert minimap coordinates to actual world coordinates
    const bounds = getNoteBounds();
    const scaleX = (bounds.maxX - bounds.minX) / minimapSize;
    const scaleY = (bounds.maxY - bounds.minY) / minimapSize;

    const worldX = bounds.minX + (x * scaleX);
    const worldY = bounds.minY + (y * scaleY);

    onNavigateToNote(worldX, worldY);
  }, [containerRef, onNavigateToNote, getNoteBounds]);

  if (!isVisible || !notes.length) {
    return null;
  }

  const bounds = getNoteBounds();
  const scaleX = minimapSize / (bounds.maxX - bounds.minX);
  const scaleY = minimapSize / (bounds.maxY - bounds.minY);

  return (
    <div
      ref={minimapRef}
      style={{
        position: 'fixed',
        bottom: '35px',
        right: '20px',
        width: minimapSize + 20,
        height: minimapSize + 20,
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '2px solid #dee2e6',
        cursor: 'crosshair',
        transition: 'opacity 0.3s ease',
        padding: '10px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
      }}
      onClick={handleMinimapClick}
    >
      {/* Minimap content with actual positions */}
      <div style={{
        position: 'relative',
        width: minimapSize,
        height: minimapSize,
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        {/* Notes positioned based on their actual coordinates */}
        {notes.map((note) => {
          const x = ((note.positionX || 0) - bounds.minX) * scaleX;
          const y = ((note.positionY || 0) - bounds.minY) * scaleY;
          
          // Use a uniform scale to maintain aspect ratio
          const uniformScale = Math.min(scaleX, scaleY);
          const width = Math.max(4, (note.width || 250) * uniformScale);
          const height = Math.max(4, (note.height || 180) * uniformScale);

          return (
            <div
              key={note.id}
              style={{
                position: 'absolute',
                left: `${Math.max(0, Math.min(minimapSize - width, x))}px`,
                top: `${Math.max(0, Math.min(minimapSize - height, y))}px`,
                width: `${width}px`,
                height: `${height}px`,
                backgroundColor: note.color || '#ffd43b',
                borderRadius: '1px',
                border: '1px solid rgba(0,0,0,0.3)',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onNavigateToNote(note.positionX || 0, note.positionY || 0);
              }}
              title={note.title || 'Untitled Note'}
            />
          );
        })}
        
        {/* Simple label */}
        <div style={{
          position: 'absolute',
          bottom: '4px',
          left: '4px',
          fontSize: '10px',
          color: '#666',
          background: 'rgba(255,255,255,0.9)',
          padding: '2px 4px',
          borderRadius: '2px',
          pointerEvents: 'none'
        }}>
          {notes.length} notes
        </div>
      </div>
    </div>
  );
};

export default Minimap;